import { useEffect, useRef, useState } from "react";
import { URL } from "../../common/api/urls";
import { useAxiosWithAuth } from "../../common/api/hooks/useAxiosWithAuth";
import {
    Box,
    Select,
    Typography,
    Container,
    CircularProgress,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
    TEXT_MSGS,
    USER_ROLE_FIELDS,
    USER_RESOURCES_LIST,
    MenuProps,
} from "../../common/utility/constant";
import { styled, useTheme, alpha } from "@mui/material/styles";
import { WATERMARK_CONFIG } from "../../common/utility/constant";
import { ToolbarFixed } from "../../layouts/component.js";
import useAuth from "../../common/hooks/useAuth";
import usePermissions from "../../common/hooks/usePermissions";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import mermaid from "mermaid";
import { Bar } from "react-chartjs-2";
import AppErrorPopup from "../../components/AppErrorPopup";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as TooltipJS,
    Legend,
} from "chart.js";

import {
    getUserListColumnDef,
    defaultColDef,
} from "../../common/utility/user_list_columnDefs";
import UserOrdersDialog from "../../common/utility/user_admin_dialog.js";

// Register once at module level (was being re-registered on every render)
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    TooltipJS,
    Legend,
);
mermaid.initialize({ startOnLoad: false });

const ROW_HEIGHT = 46;
const HEADER_HEIGHT = 44;
const GROUP_HEADER_HEIGHT = 30;

const userAdminGridSx = {
    height: 640,
    width: "100%",
    borderRadius: 1.5,
    overflow: "hidden",
    "& .ag-cell": {
        overflow: "hidden !important",
        display: "flex !important",
        alignItems: "center",
    },
    "& .ag-cell-wrapper, & .ag-cell-value": {
        width: "100%",
        minWidth: 0,
        overflow: "hidden !important",
    },
};

const gridOptions = {
    suppressColumnVirtualisation: true,
    suppressMovableColumns: true,
    suppressColumnMoveAnimation: true,
    enableCellTextSelection: true,
    ensureDomOrder: true,
};
const frameworkComponents = {};

const FooterToolbar = styled(ToolbarFixed)(
    () => `
    bottom:0px;
    justify-content:center;
    text-align:center;
  `,
);

// Users table section sizing
const FIELD_H = 40;
const btnSx = {
    height: FIELD_H,
    fontSize: 13,
    textTransform: "none",
    px: 2.5,
    whiteSpace: "nowrap",
    color: "#ffffff",
    "&.MuiButton-outlined": {
        borderColor: "rgba(255,255,255,0.45)",
        "&:hover": { borderColor: "#ffffff" },
    },
};

// Compact sizing for the top section (order / user / admin query)
const CH = 30;
const cFieldSx = {
    "& .MuiInputBase-root": { height: CH, fontSize: 14 },
    "& .MuiInputLabel-root": { fontSize: 14 },
    "& .MuiInputLabel-shrink": { fontSize: 15 },
};
const cSelectSx = { height: CH, fontSize: 14 };
const cBtnSx = {
    height: CH,
    fontSize: 14,
    px: 1.25,
    minWidth: 0,
    whiteSpace: "nowrap",
    color: "#ffffff",
};
const jsonBoxSx = {
    m: 0,
    p: 0.75,
    maxHeight: 380,
    overflow: "auto",
    fontSize: 14,
    lineHeight: 1.35,
};

// Used by Users table section
const Section = ({ title, subtitle, actions, children, sx }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: "background.paper",
            backgroundImage: "none",
            ...sx,
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
            }}
        >
            <Box>
                <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, lineHeight: 1.3 }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mt: 0.25 }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
            {actions}
        </Box>
        {children}
    </Paper>
);

// Compact panel, used by the admin query block
const CPanel = ({ title, hint, children, sx }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: "background.paper",
            backgroundImage: "none",
            minWidth: 0,
            ...sx,
        }}
    >
        {title && (
            <Typography
                sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    mb: 0.5,
                }}
            >
                {title}
                {hint && (
                    <Box
                        component="span"
                        sx={{
                            color: "text.secondary",
                            fontWeight: 400,
                            ml: 1,
                            fontSize: 14,
                        }}
                    >
                        {hint}
                    </Box>
                )}
            </Typography>
        )}
        {children}
    </Paper>
);

// ============================
// Mermaid Component
// ============================
const MermaidRenderer = ({ code }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) {
            ref.current.removeAttribute("data-processed");
            // mermaid.run is async - a sync try/catch never caught anything
            mermaid
                .run({ nodes: [ref.current] })
                .catch((e) => console.error("Mermaid rendering error:", e));
        }
    }, [code]);

    return (
        <div className="mermaid" ref={ref}>
            {code}
        </div>
    );
};

// ==========================
// Chart Renderer Component
// ==========================
const ChartRenderer = ({ value }) => {
    let rawConfig;

    try {
        rawConfig = JSON.parse(value);
    } catch (err) {
        return <pre style={{ color: "red" }}>Invalid JSON: {err.message}</pre>;
    }

    const config = rawConfig.chart || rawConfig;
    const type = config.type || config.chartType || "bar";
    const data = config.data || {
        labels: config.labels || [],
        datasets: config.datasets || [],
    };
    const options = config.options || {
        responsive: true,
        plugins: { title: { display: true, text: config.title || "Chart" } },
    };

    if (type !== "bar") {
        return (
            <pre style={{ color: "orange" }}>Only bar charts are supported</pre>
        );
    }

    return (
        <div style={{ margin: "0.75rem 0" }}>
            <Bar data={data} options={options} />
        </div>
    );
};

// ============================
// Markdown Renderer
// ============================
const MarkdownRenderer = ({ content }) => {
    if (!content) return null;
    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
                code({ className = "", children }) {
                    const lang = className.replace("language-", "");
                    const raw = Array.isArray(children)
                        ? children.join("")
                        : String(children);

                    if (lang === "mermaid" && raw.trim().startsWith("graph")) {
                        return <MermaidRenderer code={raw.trim()} />;
                    }

                    // Precedence fix: the old expression was `A && B || C || D`, which treated
                    // ANY code block containing "type" or "datasets" as a chart.
                    const isJSONChart =
                        ["json", "chart", "mermaid"].includes(lang) &&
                        (raw.includes('"chartType"') ||
                            raw.includes('"type"') ||
                            raw.includes('"datasets"'));

                    if (isJSONChart) {
                        return <ChartRenderer value={raw} />;
                    }

                    return <code className={className}>{children}</code>;
                },
            }}
        >
            {content}
        </ReactMarkdown>
    );
};

export default function UserAdmin() {
    const navigate = useNavigate();
    const theme = useTheme();
    const from = "/login";
    const { auth } = useAuth();
    const [gridAPIOptions, setGridAPIOptions] = useState();
    const [columnAPIOptions, setColumnAPIOptions] = useState();

    // Initialised to "" so the inputs are controlled from the first render
    const [ordersUser, setOrdersUser] = useState(null); // user whose order history dialog is open
    const [orderId, setOrderId] = useState("");
    const [refNo, setRefNo] = useState("");
    const [emailId, setEmailId] = useState("");
    const [userAdminQuery, setUserAdminQuery] = useState("");
    const [planName, setPlanName] = useState("");
    const [filterPlanName, setFilterPlanName] = useState("");
    const [filterRoleName, setFilterRoleName] = useState("");
    const [filterResourceName, setFilterResourceName] = useState("");
    const [payType, setPayType] = useState("");
    const [amount, setAmount] = useState("");

    const [cancel, responseData, error, loaded, reset, executeAPI] =
        useAxiosWithAuth();
    const [
        cancelOrderReq,
        responseOrderReq,
        errorOrderReq,
        loadedOrderReq,
        resetOrderReq,
        getOrderStatus,
    ] = useAxiosWithAuth();
    const [
        cancelPlanSubReq,
        responsePlanSubReq,
        errorPlanSubReq,
        loadedPlanSubReq,
        resetPlanSubReq,
        getPlanSubStatus,
    ] = useAxiosWithAuth();
    const [
        cancelAdminQueryReq,
        responseAdminQueryReq,
        errorAdminQueryReq,
        loadedAdminQueryReq,
        resetAdminQueryReq,
        getUserAdminQueryResponse,
    ] = useAxiosWithAuth();

    const permissions = usePermissions();
    const isAdmin = permissions.isAdmin();
    const gridRef = useRef();

    const [state, setState] = useState({
        isLoading: true,
        data: undefined,
        user_stats: undefined,
    });
    const { isLoading, data, user_stats } = state;

    const [msgState, setMsgState] = useState({
        open: false,
        msg: "",
        severity: "info",
    });
    const { open, msg, severity } = msgState;

    const [orderState, setOrderState] = useState({
        order_available: false,
        order_data: undefined,
        cca_data: undefined,
    });
    const { order_available, order_data, cca_data } = orderState;

    const [planState, setPlanState] = useState({
        is_valid: false,
        user_data: undefined,
        sub_data: undefined,
        planList: [
            "NSE_PREMIUM_PLUS_INDEX_MONTHLY_1",
            "NSE_PREMIUM_PLUS_INDEX_MONTHLY_3",
            "NSE_PREMIUM_PLUS_INDEX_MONTHLY_6",
            "NSE_PREMIUM_PLUS_INDEX_MONTHLY_12",
            "NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_1",
            "NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_3",
            "NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_6",
            "NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_12",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_1",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_3",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_6",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_12",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_1",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_3",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_6",
            "NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12",
            "BSE_PREMIUM_PLUS_INDEX_MONTHLY_1",
            "BSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_1",
            "BSE_NSE_PREMIUM_PLUS_INDEX_MONTHLY_1",
            "BSE_NSE_PREMIUM_PLUS_INDEX_MONTHLY_3",
            "BSE_NSE_PREMIUM_PLUS_INDEX_MONTHLY_6",
            "BSE_NSE_PREMIUM_PLUS_INDEX_MONTHLY_12",
            "BSE_NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_1",
            "BSE_NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_3",
            "BSE_NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_6",
            "BSE_NSE_PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_12",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_1",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_3",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_6",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_12",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_1",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_3",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_6",
            "BSE_NSE_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12",
            "VLD_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_1",
            "VLD_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_3",
            "VLD_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12",
            "VMR_ECOURSE",
            "VMR_VTRENDER_ECOURSE",
            "VMR_PREMIUM_PLUS_MONTHLY_1",
            "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_2",
            "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_8",
            "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_15",
            "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_1",
            "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_3",
            "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12",
            "NFP_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12",
        ],
        roleList: [
            USER_ROLE_FIELDS.BASIC,
            USER_ROLE_FIELDS.PREMIUM,
            USER_ROLE_FIELDS.PRO1,
        ],
        resourceList: [
            USER_RESOURCES_LIST.E_COURSE,
            "orderflow_both",
            USER_RESOURCES_LIST.OF_INDEX,
            USER_RESOURCES_LIST.OF_STOCKS,
            USER_RESOURCES_LIST.BSE_MP_INDEX,
            USER_RESOURCES_LIST.BSE_OF_INDEX,
        ],
        payList: ["UPI", "CCAVENUE", "PAYNIMO"],
    });
    const {
        is_valid,
        user_data,
        sub_data,
        planList,
        roleList,
        resourceList,
        payList,
    } = planState;

    const [adminQueryState, setAdminQueryState] = useState({
        is_success: false,
        response_data: undefined,
    });
    const { is_success, response_data } = adminQueryState;

    const [rowData, setRowData] = useState(data);
    const [userColumnDef, setUserColumnDef] = useState(getUserListColumnDef());

    const stateRef = useRef(state);
    const orderStateRef = useRef(orderState);
    const planStateRef = useRef(planState);
    const adminQueryStateRef = useRef(adminQueryState);
    const pendingGridResetRef = useRef(false);

    const resetAgGridFilters = (api) => {
        if (!api) return;
        api.setFilterModel(null);
        if (typeof api.resetQuickFilter === "function") {
            api.resetQuickFilter();
        }
        api.onFilterChanged();
    };

    const handleClose = () => {
        setMsgState({ ...msgState, open: false });
    };

    const onGridReady = (params) => {
        setGridAPIOptions(params.api);
        setColumnAPIOptions(params.columnApi);
    };

    const handleOrderIdChange = (event) => setOrderId(event.target.value);
    const handleRefNoChange = (event) => setRefNo(event.target.value);
    const handleEmailIdChange = (event) => setEmailId(event.target.value);
    const handleAdminQueryChange = (event) =>
        setUserAdminQuery(event.target.value);
    const handlePlanNameChange = (event) => setPlanName(event.target.value);
    const handlePayTypeChange = (event) => setPayType(event.target.value);
    const handleAmountChange = (event) => setAmount(event.target.value);

    const handleFilterPlanNameChange = (event) => {
        setFilterPlanName(event.target.value);
        setFilterRoleName("");
        setFilterResourceName("");
        executeAPI(`${URL.USER_DATA}`, "POST", {
            plan_name: event.target.value,
        });
    };

    const handleFilterRoleNameChange = (event) => {
        setFilterRoleName(event.target.value);
        setFilterPlanName("");
        setFilterResourceName("");
        executeAPI(`${URL.USER_DATA}`, "POST", {
            role_name: event.target.value,
        });
    };

    const handleFilterResourceNameChange = (event) => {
        setFilterResourceName(event.target.value);
        setFilterPlanName("");
        setFilterRoleName("");
        executeAPI(`${URL.USER_DATA}`, "POST", {
            resource_name: event.target.value,
        });
    };

    const GetOrderStatus = () => {
        let data = { order_id: orderId };
        if (refNo != undefined && refNo != "") data.ref_no = refNo;
        getOrderStatus(`${URL.ORDER_DATA}`, "POST", data);
    };

    const ClearOrderForm = () => {
        setOrderId("");
        setRefNo("");
        setOrderState((prevState) => ({
            ...prevState,
            order_data: undefined,
            cca_data: undefined,
            order_available: false,
        }));
    };

    const CheckUserDetails = () => {
        let data = { email_id: emailId };
        getPlanSubStatus(`${URL.USER_SUB_CHECK}`, "POST", data);
    };

    const GetUserAdminQueryResponse = () => {
        let data = { admin_query: userAdminQuery };
        getUserAdminQueryResponse(`${URL.USER_ADMIN_QUERY}`, "POST", data);
    };

    const CheckUserPlanDetails = (exec) => {
        let data = {
            email_id: emailId,
            plan_name: planName,
            type: payType,
            exec_cmd: exec,
        };
        if (orderId != undefined && orderId != "") data.order_id = orderId;
        if (refNo != undefined && refNo != "") data.ref_no = refNo;
        if (amount != undefined && amount != "") data.amount = amount;
        getPlanSubStatus(`${URL.USER_SUB_CHECK}`, "POST", data);
    };

    const ClearPlanForm = () => {
        setEmailId("");
        setPlanName("");
        setPayType("");
        setAmount("");
        setPlanState((prevState) => ({
            ...prevState,
            is_valid: false,
            user_data: undefined,
            sub_data: undefined,
        }));
        ClearOrderForm();
    };

    const ClearSearchForm = () => {
        setFilterPlanName("");
        setFilterRoleName("");
        setFilterResourceName("");
        pendingGridResetRef.current = true;
        resetAgGridFilters(gridRef.current?.api ?? gridAPIOptions);
        executeAPI(`${URL.USER_DATA}`, "POST", {});
    };

    const ClearAdminQueryField = () => {
        setUserAdminQuery("");
        setAdminQueryState((prevState) => ({
            ...prevState,
            is_success: false,
            response_data: undefined,
        }));
    };

    useEffect(() => {
        executeAPI(`${URL.USER_DATA}`, "POST", {});
    }, []);

    useEffect(() => {
        if (loaded) {
            console.log("loaded data ", responseData);
            if (responseData != null) {
                setState((prevState) => ({
                    ...prevState,
                    data: responseData.data ?? [],
                    user_stats: responseData.stats ?? prevState.user_stats,
                    isLoading: false,
                }));
                setRowData(responseData.data ?? []);
                if (pendingGridResetRef.current) {
                    pendingGridResetRef.current = false;
                    resetAgGridFilters(gridRef.current?.api ?? gridAPIOptions);
                }
            } else if (error !== null) {
                console.log("Error data=", error);
                setMsgState({
                    open: true,
                    msg:
                        error?.response?.data?.message ??
                        `${TEXT_MSGS.NETWORK_ERROR_MSG}`,
                    severity: "error",
                });
                if (
                    error?.response?.status === 401 ||
                    error?.response?.status === 403
                ) {
                    navigate(from, { replace: true });
                }
            }
            reset();
        }
    }, [loaded, responseData]);

    useEffect(() => {
        orderStateRef.current = orderState;

        if (loadedOrderReq) {
            console.log("loaded data ", responseOrderReq);
            if (responseOrderReq != null) {
                setOrderState((prevState) => ({
                    ...prevState,
                    order_data: responseOrderReq.data,
                    cca_data: responseOrderReq.cca_data,
                    order_available: responseOrderReq.success,
                }));
            } else if (errorOrderReq !== null) {
                console.log("Error data=", errorOrderReq);
                setMsgState({
                    open: true,
                    msg:
                        errorOrderReq?.response?.data?.message ??
                        `${TEXT_MSGS.NETWORK_ERROR_MSG}`,
                    severity: "error",
                });
                if (
                    errorOrderReq?.response?.status === 401 ||
                    errorOrderReq?.response?.status === 403
                ) {
                    navigate(from, { replace: true });
                }
            }
            resetOrderReq();
        }
    }, [loadedOrderReq, responseOrderReq]);

    useEffect(() => {
        planStateRef.current = planState;

        if (loadedPlanSubReq) {
            console.log("loaded data ", responsePlanSubReq);
            if (responsePlanSubReq != null) {
                setPlanState((prevState) => ({
                    ...prevState,
                    is_valid: responsePlanSubReq.success,
                    user_data: responsePlanSubReq.user_data,
                    sub_data: responsePlanSubReq.sub_data,
                }));
                if (payType == "CCAVENUE") {
                    setOrderState((prevState) => ({
                        ...prevState,
                        order_data: responsePlanSubReq.data,
                        cca_data: responsePlanSubReq.cca_data,
                        order_available: responsePlanSubReq.success,
                    }));
                }
            } else if (errorPlanSubReq !== null) {
                console.log("Error data=", errorPlanSubReq);
                setMsgState({
                    open: true,
                    msg:
                        errorPlanSubReq?.response?.data?.message ??
                        `${TEXT_MSGS.NETWORK_ERROR_MSG}`,
                    severity: "error",
                });
                if (
                    errorPlanSubReq?.response?.status === 401 ||
                    errorPlanSubReq?.response?.status === 403
                ) {
                    navigate(from, { replace: true });
                }
            }
            resetPlanSubReq();
        }
    }, [loadedPlanSubReq, responsePlanSubReq]);

    useEffect(() => {
        document.title = "User Admin";
    }, []);

    // BUG FIX: the old version closed `if(loadedAdminQueryReq){` too early, so the
    // `else if(errorAdminQueryReq...)` branch hung off the wrong `if`, ran on every
    // effect pass, read the error message from errorPlanSubReq, and reset() ran
    // even when nothing had loaded.
    useEffect(() => {
        adminQueryStateRef.current = adminQueryState;
        if (loadedAdminQueryReq) {
            console.log("loaded data ", responseAdminQueryReq);
            if (responseAdminQueryReq != null) {
                setAdminQueryState((prevState) => ({
                    ...prevState,
                    is_success: responseAdminQueryReq.success,
                    response_data: responseAdminQueryReq.response_data,
                }));
            } else if (errorAdminQueryReq !== null) {
                console.log("Error data=", errorAdminQueryReq);
                setMsgState({
                    open: true,
                    msg:
                        errorAdminQueryReq?.response?.data?.message ??
                        `${TEXT_MSGS.NETWORK_ERROR_MSG}`,
                    severity: "error",
                });
                if (
                    errorAdminQueryReq?.response?.status === 401 ||
                    errorAdminQueryReq?.response?.status === 403
                ) {
                    navigate(from, { replace: true });
                }
            }
            resetAdminQueryReq();
        }
    }, [loadedAdminQueryReq, responseAdminQueryReq]);

    // Plan count helper (safe if a key is missing)
    const c = (key) => user_stats?.plan_specific_count?.[key] ?? 0;
    const vmrTotal =
        c("VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_1") +
        c("VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_3") +
        c("VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12");

    // "INDEX_count:intl / STOCKS_count:intl" text for one term
    const proLine = (m) =>
        `${c(`PREMIUM_PLUS_INDEX_MONTHLY_${m}`)}:${c(
            `PREMIUM_PLUS_INDEX_INTERNATIONAL_MONTHLY_${m}`,
        )} / ${c(`PREMIUM_PLUS_INDEX_STOCKS_MONTHLY_${m}`)}:${c(
            `PREMIUM_PLUS_INDEX_STOCKS_INTERNATIONAL_MONTHLY_${m}`,
        )}`;
    const eliteLine = (m) =>
        `${c(`PREMIUM_PLUS_ORDERFLOW_INDEX_MONTHLY_${m}`)}:${c(
            `PREMIUM_PLUS_ORDERFLOW_INDEX_INTERNATIONAL_MONTHLY_${m}`,
        )} / ${c(`PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_${m}`)}:${c(
            `PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_INTERNATIONAL_MONTHLY_${m}`,
        )}`;

    const clearBtn = (onClick) => (
        <Button variant="contained" onClick={onClick} sx={cBtnSx}>
            Clear
        </Button>
    );

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "calc(100% - 64px)",
                    overflowY: "auto",
                    pb: 10,
                }}
            >
                {rowData != undefined && user_stats ? (
                    <Box
                        sx={{
                            // maxWidth: 1600,
                            mx: "30px",
                            mt: "30px",
                            px: { xs: 1, md: 1.5 },
                            py: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {/* ============ Order lookup ============ */}
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <TextField
                                size="small"
                                label="Order ID"
                                value={orderId}
                                onChange={handleOrderIdChange}
                                sx={{ ...cFieldSx, width: 260 }}
                            />
                            <TextField
                                size="small"
                                label="Reference No"
                                value={refNo}
                                onChange={handleRefNoChange}
                                sx={{ ...cFieldSx, width: 180 }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => GetOrderStatus()}
                                sx={cBtnSx}
                            >
                                Get Order Status Details
                            </Button>
                            {clearBtn(() => ClearOrderForm())}
                        </Box>

                        {/* ============ Stats text + order JSON ============ */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 3,
                                mt: "20px",
                            }}
                        >
                            <Typography
                                component="div"
                                sx={{
                                    fontSize: 16,
                                    lineHeight: 1.4,
                                    flexShrink: 0,
                                    "& .ind": { pl: 3 },
                                }}
                            >
                                <div>
                                    There are currently{" "}
                                    {user_stats.active_users} Verified and
                                    Active Users out of {user_stats.total_users}{" "}
                                    total Users.
                                </div>
                                <div>
                                    Number of users active in last 3 months:{" "}
                                    {user_stats.recently_active_users}.
                                </div>
                                <div>
                                    Number of users active in last 30 days:{" "}
                                    {user_stats.active_users_30days}.
                                </div>
                                <div>
                                    Number of premium/total users, active at the
                                    moment:{" "}
                                    {user_stats.atm_active_subscriber_count}/
                                    {user_stats.atm_active_user_count} || active
                                    today:{" "}
                                    {user_stats.today_active_subscriber_count}/
                                    {user_stats.today_active_user_count}
                                </div>
                                <div>
                                    Plan wise subscriptions breakup for{" "}
                                    {user_stats.total_subscribers} users (
                                    {user_stats.premium_count} Professional [={" "}
                                    {user_stats.premium_count_idx} Index +{" "}
                                    {user_stats.premium_count_stk} Stocks] &{" "}
                                    {user_stats.premium_plus_count} Elite [={" "}
                                    {user_stats.premium_plus_count_idx} Index
                                    with Orderflow +{" "}
                                    {user_stats.premium_plus_count_stk} Stocks
                                    with Orderflow]):
                                </div>
                                <div>
                                    Professional Plan subscriptions
                                    (:INTERNATIONAL_COUNT):
                                </div>
                                <div className="ind">
                                    1 Month: INDEX / INDEX_STOCKS: {proLine(1)}
                                </div>
                                <div className="ind">
                                    3 Months: INDEX / INDEX_STOCKS: {proLine(3)}
                                </div>
                                <div className="ind">
                                    6 Months: INDEX / INDEX_STOCKS: {proLine(6)}
                                </div>
                                <div className="ind">
                                    12 Months: INDEX / INDEX_STOCKS:{" "}
                                    {proLine(12)}
                                </div>
                                <div>
                                    Elite Plan subscriptions
                                    (:INTERNATIONAL_COUNT):
                                </div>
                                <div className="ind">
                                    1 Month: INDEX_OF / INDEX_STOCKS_OF:{" "}
                                    {eliteLine(1)}
                                </div>
                                <div className="ind">
                                    3 Months: INDEX_OF / INDEX_STOCKS_OF:{" "}
                                    {eliteLine(3)}
                                </div>
                                <div className="ind">
                                    6 Months: INDEX_OF / INDEX_STOCKS_OF:{" "}
                                    {eliteLine(6)}
                                </div>
                                <div className="ind">
                                    12 Months: INDEX_OF / INDEX_STOCKS_OF:{" "}
                                    {eliteLine(12)}
                                </div>
                                <div>
                                    BSE subscriptions (MP / OF):{" "}
                                    {(user_stats.bse_total ?? 0) -
                                        (user_stats.bse_orderflow ?? 0)}{" "}
                                    / {user_stats.bse_orderflow ?? 0}
                                </div>
                                <div>
                                    ECourse subscriptions (paid via Vtrender
                                    Charts): {c("VMR_ECOURSE")}
                                </div>
                                <br/>
                                <div>
                                    VMR subscriptions (Total: {vmrTotal}):
                                </div>
                                <div className="ind">
                                    1 Month:{" "}
                                    {c(
                                        "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_1",
                                    )}
                                </div>
                                <div className="ind">
                                    3 Months:{" "}
                                    {c(
                                        "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_3",
                                    )}
                                </div>
                                <div className="ind">
                                    12 Months:{" "}
                                    {c(
                                        "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_STOCKS_MONTHLY_12",
                                    )}
                                </div>
                                <div>
                                    Complementary with ECourse,
                                    VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_8
                                    (active count):{" "}
                                    {c(
                                        "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_8",
                                    )}
                                </div>
                                <div>
                                    Complementary with ECourse,
                                    VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_15
                                    (active count):{" "}
                                    {c(
                                        "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_15",
                                    )}
                                </div>
                                <div>
                                    Demo plan,
                                    VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_2
                                    (active / total_done):{" "}
                                    {c(
                                        "VMR_PREMIUM_PLUS_ORDERFLOW_INDEX_DAILY_2",
                                    )}{" "}
                                    / {user_stats.total_demo_done ?? 0}
                                </div>
                            </Typography>

                            {order_available && (
                                <>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 12 }}>
                                            Order Details in DB:
                                        </Typography>
                                        <Box component="pre" sx={jsonBoxSx}>
                                            {JSON.stringify(
                                                order_data,
                                                null,
                                                2,
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 12 }}>
                                            Order Details from CCAvenues server:
                                        </Typography>
                                        <Box component="pre" sx={jsonBoxSx}>
                                            {JSON.stringify(cca_data, null, 2)}
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>

                        {/* ============ User details / subscribe ============ */}
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 1,
                                mt: "20px",
                            }}
                        >
                            <TextField
                                required
                                size="small"
                                label="Email ID"
                                value={emailId}
                                onChange={handleEmailIdChange}
                                sx={{ ...cFieldSx, width: 240 }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => CheckUserDetails()}
                                sx={cBtnSx}
                            >
                                Check User Details
                            </Button>
                            <FormControl
                                required
                                size="small"
                                sx={{ width: 480, ...cFieldSx }}
                            >
                                <InputLabel id="plan-select-label">
                                    Plan Name
                                </InputLabel>
                                <Select
                                    labelId="plan-select-label"
                                    id="plan-select"
                                    value={
                                        planList.find(
                                            (item) => item == planName,
                                        ) || ""
                                    }
                                    label="Plan Name"
                                    MenuProps={MenuProps}
                                    sx={cSelectSx}
                                    onChange={handlePlanNameChange}
                                >
                                    {planList.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                required
                                size="small"
                                sx={{ width: 140, ...cFieldSx }}
                            >
                                <InputLabel id="pay-select-label">
                                    Pay Type
                                </InputLabel>
                                <Select
                                    labelId="pay-select-label"
                                    id="pay-select"
                                    value={
                                        payList.find(
                                            (item) => item == payType,
                                        ) || ""
                                    }
                                    label="Pay Type"
                                    MenuProps={MenuProps}
                                    sx={cSelectSx}
                                    onChange={handlePayTypeChange}
                                >
                                    {payList.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {(payType == "UPI" || payType == "PAYNIMO") && (
                                <TextField
                                    required
                                    size="small"
                                    label="Amount"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    sx={{ ...cFieldSx, width: 140 }}
                                />
                            )}
                            {payType == "CCAVENUE" && (
                                <>
                                    <TextField
                                        required
                                        size="small"
                                        label="Order ID"
                                        value={orderId}
                                        onChange={handleOrderIdChange}
                                        sx={{ ...cFieldSx, width: 200 }}
                                    />
                                    <TextField
                                        required
                                        size="small"
                                        label="Reference No"
                                        value={refNo}
                                        onChange={handleRefNoChange}
                                        sx={{ ...cFieldSx, width: 160 }}
                                    />
                                </>
                            )}
                            <Button
                                variant="contained"
                                onClick={() => CheckUserPlanDetails(false)}
                                sx={cBtnSx}
                            >
                                Check Order and User Details
                            </Button>
                            {clearBtn(() => ClearPlanForm())}
                        </Box>

                        {is_valid && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ minWidth: 300, maxWidth: 600, flex: 1, mt: "5px", }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: "10px" }}>
                                        User Details in DB:
                                    </Typography>
                                    <Box component="pre" sx={jsonBoxSx}>
                                        {JSON.stringify(user_data, null, 2)}
                                    </Box>
                                </Box>
                                <Box sx={{ minWidth: 0, maxWidth: 1200, flex: 1, mt: "5px", }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: "10px" }}>
                                        Plan Details in DB:
                                    </Typography>
                                    <Box component="pre" sx={jsonBoxSx}>
                                        {JSON.stringify(sub_data, null, 2)}
                                    </Box>
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={() => CheckUserPlanDetails(true)}
                                    sx={cBtnSx}
                                >
                                    Subscribe
                                </Button>
                            </Box>
                        )}

                        {/* ============ Admin data question ============ */}
                        {isAdmin && (
                            <CPanel
                                title="Ask about registration and subscription data"
                                hint="free-text question, answered with tables and charts"
                                sx={{mt: "20px",}}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1,
                                    }}
                                >
                                    <TextField
                                        required
                                        size="small"
                                        label="Your question"
                                        value={userAdminQuery}
                                        onChange={handleAdminQueryChange}
                                        sx={{
                                            ...cFieldSx,
                                            flex: 1,
                                            minWidth: 300,
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            GetUserAdminQueryResponse()
                                        }
                                        sx={cBtnSx}
                                    >
                                        Get response
                                    </Button>
                                    {clearBtn(() => ClearAdminQueryField())}
                                </Box>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        color: "text.secondary",
                                        fontSize: 11.5,
                                        lineHeight: 1.35,
                                    }}
                                >
                                    Eg: "Do a detailed analysis of the new user
                                    registrations from January 2024 to July
                                    2025." &nbsp;|&nbsp; Eg: "Analyse the data
                                    of new users registered in 2025 who
                                    subscribed to a plan and analyse which plans
                                    they purchased."
                                </Box>

                                {response_data && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 1.5,
                                            maxWidth: 1100,
                                            fontSize: 13.5,
                                            lineHeight: 1.5,
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            "& h1, & h2, & h3": {
                                                mt: 1,
                                                mb: 0.5,
                                                fontWeight: 600,
                                            },
                                            "& p, & ul, & ol": {
                                                my: 0.5,
                                            },
                                            "& table": {
                                                borderCollapse: "collapse",
                                                width: "100%",
                                                my: 1,
                                                fontSize: 12.5,
                                            },
                                            "& th, & td": {
                                                border: "1px solid",
                                                borderColor: "divider",
                                                px: 1,
                                                py: 0.3,
                                                textAlign: "left",
                                            },
                                            "& th": {
                                                bgcolor: (t) =>
                                                    alpha(
                                                        t.palette.common.white,
                                                        0.05,
                                                    ),
                                            },
                                            "& pre": { overflowX: "auto" },
                                        }}
                                    >
                                        <MarkdownRenderer
                                            content={response_data}
                                        />
                                    </Box>
                                )}
                            </CPanel>
                        )}

                        {/* ============ Users table (unchanged) ============ */}
                        <Section
                            title="Users"
                            subtitle="Only the most recently changed filter applies"
                            sx={{mt:"20px"}}
                            actions={
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={() => ClearSearchForm()}
                                    sx={btnSx}
                                >
                                    Reset filters
                                </Button>
                            }
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <FormControl size="small" sx={{ width: 220 }}>
                                    <InputLabel id="filter-role-label">
                                        Role
                                    </InputLabel>
                                    <Select
                                        labelId="filter-role-label"
                                        id="filter-role"
                                        value={
                                            roleList.find(
                                                (item) =>
                                                    item == filterRoleName,
                                            ) || ""
                                        }
                                        label="Role"
                                        MenuProps={MenuProps}
                                        sx={{ height: FIELD_H }}
                                        onChange={handleFilterRoleNameChange}
                                    >
                                        {roleList.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ width: 220 }}>
                                    <InputLabel id="filter-resource-label">
                                        Resource
                                    </InputLabel>
                                    <Select
                                        labelId="filter-resource-label"
                                        id="filter-resource"
                                        value={
                                            resourceList.find(
                                                (item) =>
                                                    item == filterResourceName,
                                            ) || ""
                                        }
                                        label="Resource"
                                        MenuProps={MenuProps}
                                        sx={{ height: FIELD_H }}
                                        onChange={
                                            handleFilterResourceNameChange
                                        }
                                    >
                                        {resourceList.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ width: 400 }}>
                                    <InputLabel id="filter-plan-label">
                                        Plan
                                    </InputLabel>
                                    <Select
                                        labelId="filter-plan-label"
                                        id="filter-plan"
                                        value={
                                            planList.find(
                                                (item) =>
                                                    item == filterPlanName,
                                            ) || ""
                                        }
                                        label="Plan"
                                        MenuProps={MenuProps}
                                        sx={{ height: FIELD_H }}
                                        onChange={handleFilterPlanNameChange}
                                    >
                                        {planList.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box
                                className="ag-theme-alpine-dark user-admin-grid"
                                sx={userAdminGridSx}
                            >
                                <AgGridReact
                                    ref={gridRef}
                                    onGridReady={onGridReady}
                                    components={frameworkComponents}
                                    rowHeight={ROW_HEIGHT}
                                    headerHeight={HEADER_HEIGHT}
                                    groupHeaderHeight={GROUP_HEADER_HEIGHT}
                                    gridOptions={gridOptions}
                                    rowData={rowData}
                                    columnDefs={userColumnDef}
                                    defaultColDef={defaultColDef}
                                    context={{ openOrders: setOrdersUser }}
                                    tooltipShowDelay={300}
                                />
                            </Box>
                        </Section>
                    </Box>
                ) : (
                    <div style={{ textAlign: "center", width: "100%" }}>
                        <CircularProgress sx={{ marginTop: "10%" }} />
                    </div>
                )}
            </Box>

            <UserOrdersDialog
                user={ordersUser}
                onClose={() => setOrdersUser(null)}
            />

            <FooterToolbar
                sx={{
                    borderTop: `1px solid ${theme.palette.primaryTheme.shade03}`,
                }}
            >
                <Container>
                    <Typography variant="copyRight" align="center">
                        {WATERMARK_CONFIG.TABLE_TEXT}
                    </Typography>
                </Container>
            </FooterToolbar>

            <AppErrorPopup
                open={open}
                message={msg}
                severity={severity}
                onClose={handleClose}
                showCloseX
            />
        </>
    );
}
